import React, { useMemo, useState, useCallback } from 'react';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import setLanguage from '../../hooks/setLanguage';
import translation from '../../signals/translations';
import IconMarkerFav from '../../icons/IconMarkerFav';
import IconMarkerSearch from '../../icons/IconMarkerSearch';

const RightPanel = () => {
	const { languages = [], basicInfo = {}, floorInfo = [] } = jsonData.value || {};
	const { activeFloor = 0 } = appState.value || {};

	const [showFloors, setShowFloors] = useState(false);
	const [showLegends, setShowLegends] = useState(false);

	const floorList = useMemo(() => {
		const floorCount = Number(basicInfo.floorCount) || 0;
		const floorList = [];
		if (Array.isArray(floorInfo)) {
			for (let i = 0; i < floorCount; i++) {
				floorList.push(floorInfo[i]);
			}
		}
		return floorList;
	}, [basicInfo.floorCount, floorInfo]);

	const currentFloor = useMemo(() => {
		if (!Array.isArray(floorList) || !floorList[activeFloor]) return undefined;
		return floorList[activeFloor];
	}, [activeFloor, floorList]);

	const setActiveFloor = (index) => {
		appState.value = {
			...appState.value,
			activeFloor: index,
		};
	};

	const setActiveFloorMobile = (index) => {
		appState.value = {
			...appState.value,
			activeFloor: index,
		};
		setShowFloors(false);
	};

	const toggleShowFloors = useCallback(() => {
		setShowLegends(false);
		setShowFloors(!showFloors);
	}, [showFloors]);

	const toggleShowLegends = useCallback(() => {
		setShowFloors(false);
		setShowLegends(!showLegends);
	}, [showLegends]);

	const toggleUI = (ui) => {
		appState.value = {
			...appState.value,
			[ui]: !appState.value[ui],
		};
	};

	const floorListOrderedByFloor = useMemo(() => {
		if (!Array.isArray(floorList) || floorList.length === 0) return [];
		const floorListWithOriginalIndex = floorList.map((floor, index) => {
			return {
				...floor,
				originalIndex: index,
			};
		});
		const floorListOrderedByFloor = [...floorListWithOriginalIndex];
		floorListOrderedByFloor.sort((a, b) => {
			return Number(a?.floor || 0) - Number(b?.floor || 0);
		});
		return floorListOrderedByFloor;
	}, [floorList]);

	const setExhibitorSection = (section) => {
		appState.value = {
			...appState.value,
			ExhibitorList: true,
			exhibitorSection: section,
		};
	};

	return (
		<div id='RightPanel'>
			<div className='section'>
				<div className='section-heading'>{translation('rightPanel.language')}</div>
				<div className='flex-buttons'>
					{languages.map((language) => {
						const { code, nameNative } = language;
						const isCurrent = code === jsonData.value.currentLanguage.code;

						return (
							<div className='button-wrap' key={code}>
								<div
									className={`button ${isCurrent ? 'active' : ''}`}
									onClick={() => {
										setLanguage(code);
									}}>
									{nameNative}
								</div>
							</div>
						);
					})}
				</div>
			</div>

			<div className='section floor'>
				<div className='section-heading'>{translation('rightPanel.floors')}</div>
				<div className='flex-buttons'>
					{Array.isArray(floorListOrderedByFloor) && floorListOrderedByFloor.length > 0 ? (
						floorListOrderedByFloor.map((floor, index) => {
							if (!floor) return null;
							return (
								<div className='button-wrap wide' key={`floor${index}`}>
									<div
										className={`button ${activeFloor === floor.originalIndex ? 'active' : ''}`}
										onClick={() => {
											setActiveFloor(floor.originalIndex);
										}}>
										{floor.name || '未命名樓層'}
									</div>
								</div>
							);
						})
					) : (
						<div className='button-wrap wide'>無樓層</div>
					)}
				</div>
			</div>

			<div className='mobile-floors'>
				<div className='current-floor' onClick={toggleShowFloors}>
					{currentFloor?.name || '無樓層資料'}
				</div>

				<div className={`other-floors ${showFloors ? 'active' : ''}`}>
					<div className='close' onClick={toggleShowFloors}>
						x
					</div>
					{Array.isArray(floorList) && floorList.length > 0 ? (
						floorList.map((floor, index) => {
							if (activeFloor === index || !floor) return null;
							return (
								<div
									className='other-floor'
									key={`mobile-floor${index}`}
									onClick={() => {
										setActiveFloorMobile(index);
									}}>
									{floor.name || '未命名樓層'}
								</div>
							);
						})
					) : (
						<div className='other-floor'>無其他樓層</div>
					)}
				</div>
			</div>

			<div className='mobile-legends'>
				<div className='toggler' onClick={toggleShowLegends}>
					{translation('mobile.mapKeys')}
				</div>

				<div className={`legends ${showLegends ? 'active' : ''}`}>
					<div className='close' onClick={toggleShowLegends}>
						x
					</div>

					<div
						className='legend-row'
						onClick={() => {
							toggleShowLegends();
							toggleUI('MyFavourites');
						}}>
						<div className='legend'>
							<IconMarkerFav />
						</div>
						<div className='legend-text'>{translation('rightPanel.keys.myFavorite')}</div>
					</div>

					<div
						className='legend-row'
						onClick={() => {
							toggleShowLegends();
							toggleUI('SearchPanel');
						}}>
						<div className='legend'>
							<IconMarkerSearch />
						</div>
						<div className='legend-text'>{translation('rightPanel.keys.searchResult')}</div>
					</div>

					{currentFloor &&
						currentFloor.sections.map((section, index) => {
							return (
								<div
									className='legend-row'
									key={`legend-row${index}`}
									onClick={() => {
										toggleShowLegends();
										setExhibitorSection(section.name);
									}}>
									<div className='legend'>
										<div className='color' style={{ backgroundColor: section.fillColor }}></div>
									</div>
									<div className='legend-text'>{section.name}</div>
								</div>
							);
						})}

					<div className='filler'></div>
					<div className='filler'></div>
					<div className='filler'></div>
					<div className='filler'></div>
				</div>
			</div>
		</div>
	);
};

export default RightPanel;
